// src/pages/AccessUserEditPage.tsx — Slice F (admin user maintenance) +
// Phase 9 (full role grid, apps map, PIN reveal, block/unblock, emergency
// reveal, emergency contact grant, home-address reveal).
//
// Admin user create / edit at /access/users/new and /access/users/:id.
// SSO-admin-only — gated client-side; server also enforces requireSsoAdmin.
//
// Phase 9 sections (edit mode only):
//   - Sign-on status pill ("active" / "pending")
//   - Block / unblock toggle (handles LAST_ADMIN 409)
//   - Role grid: Admin, HR Admin, News, Alerts, Manager, DM, MOM, EM, First Aider
//   - Apps grid: 14 per-app toggles (handles LAST_ADMIN 409 on userAccess strip)
//   - PIN reveal modal (PIN users only; 30s auto-close; audited server-side)
//   - Emergency contact reveal modal (HR / self / granted only)
//   - Home address reveal modal (HR / self only — strictest tier)
//   - Emergency contact grant multi-select
//
// PATCH payload is composed from the dirty diff so the audit log row only
// captures fields that actually changed. mergeApps logic on the backend
// preserves untouched keys; we send only the keys the admin flipped.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import type { AppPermissions } from "../auth";
import { useDepartments } from "../hooks/useDepartments";
import { C, F } from "../theme";
import {
  Button,
  Card,
  Check,
  Eyebrow,
  Field,
  Hairline,
  PermissionLoading,
  TopBar,
  Stamp,
} from "../components/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContactRow {
  id: number;
  first: string;
  last: string;
  role: string | null;
  isManager: boolean;
}

interface ContactsListResponse {
  contacts: ContactRow[];
}

interface LinkedContact {
  id: number;
  first_name: string;
  last_name: string;
}

interface AccessUserDetail {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  departments: string[];
  member_id_number: number | null;
  merac_card_no: string | null;
  // Phase 5d -- stable forensic Edition Ref. Read-only; admin display only.
  edition_ref: string | null;
  line_manager_contact_id: number | null;
  line_manager_name: string | null;
  is_admin: boolean;
  is_hr_admin: boolean;
  is_news_admin: boolean;
  // Phase 5d -- with is_news_admin, gates the CEO Newsletter Creator + CEO routing.
  is_ceo_update: boolean;
  is_alerts_admin: boolean;
  is_manager: boolean;
  is_dm: boolean;
  is_mom: boolean;
  is_em: boolean;
  // Phase 10 / Feature 1: Duty Controller role.
  is_dc: boolean;
  // Duty Fitter role (mirrors is_dc, migration 068).
  is_df: boolean;
  is_first_aider: boolean;
  // Phase 11 -- content-editor flags.
  is_docs_editor: boolean;
  is_contact_editor: boolean;
  // Documents onboarding control (migration 080).
  is_onboarding_admin: boolean;
  // Article Writer (migration 070) -- per-newsletter article-authoring scope.
  is_article_writer_ceo: boolean;
  is_article_writer_weekly: boolean;
  // Phase 7 -- Weekly Sheet Enhanced Data View. Gates customerName + notes
  // on /api/weekly-sheet items. Server emits a dedicated audit row on
  // transitions (user.perms.weekly_sheet_enhanced) alongside the generic
  // permission.change row.
  is_weekly_sheet_enhanced: boolean;
  // Sign-In System (Build 1, migration 055) -- duty sign-in eligibility (State 18).
  sign_in_enabled: boolean;
  // Driving Licence Check DV Admin role (migration 074).
  is_dvcheck_admin: boolean;
  // Long Service Awards admin role (migration 085).
  is_long_service_admin: boolean;
  // Moorsline Editor role (migration 094).
  is_moorsline_editor: boolean;
  // Vacancies Manager role (migration 169).
  is_vacancies_manager: boolean;
  // NYMR Newsletter Publisher role (migration 124).
  is_newsletter_publisher: boolean;
  // Training Admin role (migration 099).
  is_training_admin: boolean;
  // Incident Log Full Access (migration 101).
  is_incident_admin: boolean;
  // Earliest ls_service_periods.from_date (ISO) — null when not yet in the
  // LS Admin volunteer universe.
  ls_start_date: string | null;
  is_blocked: boolean;
  sign_on_status: "pending" | "active" | string;
  apps: AppPermissions;
  emergency_contact_visible_to: number[];
  show_mobile_to_all_hub_users: boolean;
  has_pin: boolean;
  has_sso: boolean;
  lockedUntil: string | null;
  isLocked: boolean;
  linkedContact: LinkedContact | null;
  last_login: string | null;
}

// Sign-In System (Build 1) -- duty role grants for the State 18 pill grid.
interface ApprovalRole {
  code: string;
  displayName: string;
  defaultLocationCode: string | null;
  granted: boolean;
  lastUsedAt: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The Departments multi-select options come from GET /api/access/departments
// via the shared useDepartments() hook (issue #15) — no longer a hardcoded
// const, which had silently drifted from dbo.departments and never picked up
// departments added through the "+ New Department" button.

// Apps grid layout — labels in the same order as the App registry.
//
// Phase 9 follow-up (2026-05-21): `headlines` row added between `alerts` and
// `duty`. Both are home-page sections (banner + headlines list), so grouping
// reads cleanly in the admin UI. Backend key added to AppPermissions +
// DEFAULT_APPS + APP_KEYS validator in the matching server-side commits.
//
// Phase 9 follow-up (2026-05-21, label polish): the `firstAider` sub-text
// previously read "Callouts" — a hangover from an earlier design where this
// toggle gated callout routing. The permission now controls whether the
// viewer sees "First aider" badges on contact rows and the corresponding
// filter pill, so the sub reads "See who's a first aider" to match what
// it actually does.
//
// Phase 9 follow-up (2026-05-22, render fix): the sub-text field was
// previously defined here but never passed to <Check>, so none of the 14
// subs ever appeared on screen. Check now accepts a `sub` prop and the
// apps-grid map below passes sub={a.sub}, so the descriptive second line
// finally renders. The Roles grid is unaffected because it calls Check
// without a sub.
const APP_KEYS: Array<{ key: keyof AppPermissions; label: string; sub: string }> = [
  { key: "contacts",   label: "Contacts",        sub: "Directory and emergency" },
  { key: "weekly",     label: "Weekly Sheet",    sub: "What's running this week" },
  { key: "trains",     label: "Live Trains",     sub: "External departures app" },
  { key: "newsletter", label: "Newsletter",      sub: "Reader" },
  { key: "newsletterReader", label: "Newsletter Reader", sub: "Read the latest NYMR Newsletter (on by default)" },
  { key: "tickets",    label: "Comp Tickets",    sub: "Allocations & visits" },
  { key: "vacancies",  label: "Vacancies",       sub: "Volunteer & paid roles" },
  { key: "docs",       label: "Documents",       sub: "Policies & notices" },
  { key: "firstAider", label: "First Aider",     sub: "See who's a first aider" },
  { key: "firstAiderCallout", label: "Call to First Aiders", sub: "Emergency dispatch tile — summon on-site first aiders" },
  { key: "alerts",     label: "Alerts",          sub: "Home-page banner" },
  { key: "headlines",  label: "Headlines",       sub: "Home-page section" },
  { key: "duty",       label: "Duty",            sub: "Roster" },
  { key: "userAccess", label: "User Access",     sub: "Admin" },
  { key: "auditLog",   label: "Audit Log",       sub: "Admin" },
  { key: "nlcreator",  label: "Newsletter Editor", sub: "Admin" },
  { key: "articleWriter", label: "Article Writer", sub: "Write newsletter articles (non-admin)" },
  { key: "drivingLicenceCheck", label: "Driving Licence Check", sub: "Driver self-service (submit DVLA code)" },
  { key: "longService", label: "Long Service Pass", sub: "Recipient's own travel pass & awards" },
  { key: "training", label: "My Training", sub: "iHasco training records & certificates — off unless enabled" },
  // Incident Log (migration 101) — READ ONLY access (view all H&S incidents).
  // FULL ACCESS is the "Incident Log — Full Access" role toggle in the Roles grid.
  { key: "incidentLog", label: "Incident Log (Read Only)", sub: "View all H&S incidents" },
  { key: "raiseIssue", label: "Raise an Issue", sub: "Standalone report-an-issue app (split from Issue Manager)" },
  // BigPlanSTN (migration 104) — signal-box iPad sign-in for The Big Plan's STNs. When on, the
  // "Signal-box assignment" panel below lets you pick which boxes this person may sign in at.
  { key: "bigPlanStn", label: "Signal-box sign-in (BigPlanSTN)", sub: "May sign in to a signal-box iPad" },
  // BigPlanDevices (migration 105) — the iPad device-registry admin app (approve/revoke iPads).
  { key: "bigPlanDevices", label: "Signal-box Devices (admin)", sub: "Manage & approve signal-box iPads" },
  // Mini-Blog (migration 128). Reader is on by default (department-filtered);
  // Creator is the Author grant — publish to your own department(s) only.
  { key: "miniBlog", label: "Mini-Blog", sub: "Read your department's mini-blog (on by default)" },
  { key: "miniBlogCreator", label: "Mini-Blog Creator", sub: "Author — post mini-blogs for their department" },
  { key: "liveTracker", label: "Live Tracker", sub: "GPS tracker fleet health & live map (admins always have access)" },
  { key: "camView", label: "CamView", sub: "On-demand station CCTV snapshots (admins always have access)" },
  { key: "camViewPro", label: "CamView Pro", sub: "Extended CCTV set — station + yard/crossing cameras (admins always have access)" },
  // Out Of Office (migration 150) — writes a customer-facing auto-reply on the
  // main public address, so this is granted deliberately, never by default.
  { key: "outOfOffice", label: "Out Of Office", sub: "Set the auto-reply on shared mailboxes such as info@ (admins always have access)" },
  // Rostering (migration 147) — personal view onto rostering.nymr.co.uk. OFF for
  // everyone by default; seeded to chris.flynn@ only. Toggle here to release it.
  { key: "rostering", label: "Rostering", sub: "Personal roster, availability, clock-on & compliance" },
];

export interface AccessUserEditPageProps {
  mode: "new" | "edit";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AccessUserEditPage({ mode }: AccessUserEditPageProps) {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const { me, loading: meLoading } = useCurrentUser();

  // Issue #15 — canonical department list (shared with AccessUsersPage).
  // null until loaded; rendered union also includes any departments still
  // on the user that have since dropped out of this list (see deptOptions).
  const { departments: allDepartments } = useDepartments();

  const userId = mode === "edit" ? Number(params.id) : null;

  // Form state.
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [signOnStatus, setSignOnStatus] = useState<string>("active");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isHrAdmin, setIsHrAdmin] = useState(false);
  const [isNewsAdmin, setIsNewsAdmin] = useState(false);
  // Phase 5d -- CEO Update role (pairs with News Admin to gate the CEO Newsletter).
  const [isCeoUpdate, setIsCeoUpdate] = useState(false);
  const [isAlertsAdmin, setIsAlertsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isDm, setIsDm] = useState(false);
  const [isMom, setIsMom] = useState(false);
  const [isEm, setIsEm] = useState(false);
  const [isDc, setIsDc] = useState(false);
  const [isDf, setIsDf] = useState(false);
  const [isDvCheckAdmin, setIsDvCheckAdmin] = useState(false);
  const [isLongServiceAdmin, setIsLongServiceAdmin] = useState(false);
  const [isMoorslineEditor, setIsMoorslineEditor] = useState(false);
  const [isVacanciesManager, setIsVacanciesManager] = useState(false);
  const [isNewsletterPublisher, setIsNewsletterPublisher] = useState(false);
  const [isTrainingAdmin, setIsTrainingAdmin] = useState(false);
  const [isIncidentAdmin, setIsIncidentAdmin] = useState(false);
  const [lsStartDate, setLsStartDate] = useState("");
  const [isFirstAider, setIsFirstAider] = useState(false);
  // Phase 11 -- content-editor flags.
  const [isDocsEditor, setIsDocsEditor] = useState(false);
  const [isContactEditor, setIsContactEditor] = useState(false);
  const [isOnboardingAdmin, setIsOnboardingAdmin] = useState(false);
  const [isArticleWriterCeo, setIsArticleWriterCeo] = useState(false);
  const [isArticleWriterWeekly, setIsArticleWriterWeekly] = useState(false);
  // Phase 7 -- Weekly Sheet Enhanced Data View per-user gate.
  const [isWeeklySheetEnhanced, setIsWeeklySheetEnhanced] = useState(false);
  // Sign-In System (Build 1) -- duty eligibility (State 18) + role grants.
  const [signInEnabled, setSignInEnabled] = useState(false);
  const [dutyRoles, setDutyRoles] = useState<ApprovalRole[] | null>(null);
  const [grantedCodes, setGrantedCodes] = useState<Set<string>>(new Set());
  const [grantsBaseline, setGrantsBaseline] = useState<Set<string>>(new Set());
  const [isBlocked, setIsBlocked] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [memberId, setMemberId] = useState<string>("");
  const [meracCardNo, setMeracCardNo] = useState<string>("");
  // Phase 5d -- read-only forensic Edition Ref (mono display only).
  const [editionRef, setEditionRef] = useState<string | null>(null);
  const [lineManagerContactId, setLineManagerContactId] = useState<number | null>(null);
  const [lineManagerName, setLineManagerName] = useState<string>("");
  const [hasContact, setHasContact] = useState<boolean>(false);
  const [hasPin, setHasPin] = useState<boolean>(false);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  const [apps, setApps] = useState<AppPermissions | null>(null);

  const [contacts, setContacts] = useState<ContactRow[] | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Block/unblock state.
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);

  // PIN reveal modal state.
  const [pinModal, setPinModal] = useState<
    null | { loading: boolean; pin: string | null; error: string | null }
  >(null);

  // Emergency reveal modal state.
  const [emergencyModal, setEmergencyModal] = useState<
    | null
    | {
        loading: boolean;
        contact: { name: string | null; relationship: string | null; phone: string | null; alt: string | null } | null;
        empty: boolean;
        error: string | null;
      }
  >(null);

  // Home-address reveal modal state. HR-only (canViewHomeAddress server-side).
  const [addressModal, setAddressModal] = useState<
    | null
    | {
        loading: boolean;
        address: { line1: string | null; line2: string | null; postcode: string | null; homePhone: string | null } | null;
        empty: boolean;
        error: string | null;
      }
  >(null);

  // Delete confirmation state.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Track baseline so we only send diffed flags on save.
  const [baseline, setBaseline] = useState<AccessUserDetail | null>(null);

  const ssoGate = me && me.isAdmin && !me.isSsoAdmin;

  // Load existing user in edit mode.
  useEffect(() => {
    if (mode !== "edit" || userId === null || Number.isNaN(userId)) return;
    let cancelled = false;
    apiFetch<AccessUserDetail>(`/api/access/users/${userId}`)
      .then((u) => {
        if (cancelled) return;
        setFirst(u.first_name);
        setLast(u.last_name);
        setEmail(u.email);
        setSignOnStatus(u.sign_on_status);
        setIsAdmin(u.is_admin);
        setIsHrAdmin(u.is_hr_admin);
        setIsNewsAdmin(u.is_news_admin);
        setIsCeoUpdate(u.is_ceo_update);
        setIsAlertsAdmin(u.is_alerts_admin);
        setIsManager(u.is_manager);
        setIsDm(u.is_dm);
        setIsMom(u.is_mom);
        setIsEm(u.is_em);
        setIsDc(u.is_dc);
        setIsDf(u.is_df);
        setIsDvCheckAdmin(u.is_dvcheck_admin);
        setIsLongServiceAdmin(u.is_long_service_admin);
        setIsMoorslineEditor(u.is_moorsline_editor);
        setIsVacanciesManager(!!u.is_vacancies_manager);
        setIsNewsletterPublisher(u.is_newsletter_publisher);
        setIsTrainingAdmin(u.is_training_admin);
        setIsIncidentAdmin(u.is_incident_admin);
        setLsStartDate(u.ls_start_date ?? "");
        setIsFirstAider(u.is_first_aider);
        setIsDocsEditor(u.is_docs_editor);
        setIsContactEditor(u.is_contact_editor);
        setIsOnboardingAdmin(u.is_onboarding_admin);
        setIsArticleWriterCeo(u.is_article_writer_ceo);
        setIsArticleWriterWeekly(u.is_article_writer_weekly);
        setIsWeeklySheetEnhanced(u.is_weekly_sheet_enhanced);
        setSignInEnabled(u.sign_in_enabled);
        setIsBlocked(u.is_blocked);
        setDepartments(u.departments);
        setMemberId(u.member_id_number !== null ? String(u.member_id_number) : "");
        setMeracCardNo(u.merac_card_no ?? "");
        setEditionRef(u.edition_ref);
        setLineManagerContactId(u.line_manager_contact_id);
        setLineManagerName(u.line_manager_name ?? "");
        setHasContact(u.linkedContact !== null);
        setHasPin(u.has_pin);
        setLockedUntil(u.lockedUntil);
        setIsLocked(u.isLocked);
        setApps(u.apps);
        setBaseline(u);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof ApiError ? e.message : "Couldn't load user.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, userId]);


  // Load contacts for the line-manager picker.
  useEffect(() => {
    let cancelled = false;
    apiFetch<ContactsListResponse>("/api/contacts")
      .then((res) => {
        if (!cancelled) setContacts(res.contacts);
      })
      .catch(() => {
        if (!cancelled) setContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sign-In System (Build 1) -- load duty role grants for the State 18 grid.
  useEffect(() => {
    if (mode !== "edit" || userId === null || Number.isNaN(userId)) return;
    let cancelled = false;
    apiFetch<{ roles: ApprovalRole[] }>(`/api/timesheet/approvals?user=${userId}`)
      .then((res) => {
        if (cancelled) return;
        setDutyRoles(res.roles);
        const granted = new Set(res.roles.filter((r) => r.granted).map((r) => r.code));
        setGrantedCodes(granted);
        setGrantsBaseline(new Set(granted));
      })
      .catch(() => {
        if (!cancelled) setDutyRoles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, userId]);

  // Phase 10 / Feature 3 — show every manager-flagged contact, not just
  // the first 12. The flag (`isManager`) was already on /api/contacts
  // since migration 033; the cap is what was actually blocking acceptance
  // ("User B appears IF AND ONLY IF flagged"). Sorted by last + first so
  // the pill row is predictable regardless of insertion order in the
  // contacts list.
  const managerOptions = useMemo(() => {
    if (!contacts) return [];
    return [...contacts]
      .filter((c) => c.isManager)
      .sort(
        (a, b) =>
          a.last.localeCompare(b.last) || a.first.localeCompare(b.first),
      );
  }, [contacts]);

  // Issue #15 — render the union of the canonical department list and any
  // departments already on this user that are NOT in that list (e.g. an old
  // "Safety" value seeded before the list was trimmed). The extras stay
  // visible and de-selectable; without them, buildPatchBody's array diff
  // would silently drop a held-but-now-uncanonical department on save.
  const deptOptions = useMemo(() => {
    const canonical = allDepartments?.map((d) => d.name) ?? [];
    const extras = departments.filter((d) => !canonical.includes(d));
    return [...canonical, ...extras];
  }, [allDepartments, departments]);

  if (meLoading) {
    return <PermissionLoading title="Users" onBack={() => navigate("/access/users")} />;
  }
  if (ssoGate) {
    return (
      <div>
        <TopBar title="Users" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 17,
                fontWeight: 700,
                color: C.ink,
                marginBottom: 6,
              }}
            >
              This page is for SSO admins only.
            </div>
            <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={() => navigate("/apps")}>
              Back to apps
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const editing = mode === "edit";

  // PATCH body builder — only sends fields whose current value differs
  // from the baseline. Skips identity (server rejects), and excludes
  // signOnStatus (server-owned).
  function buildPatchBody(): Record<string, unknown> {
    if (!baseline || !apps) return {};
    const out: Record<string, unknown> = {};

    if (isAdmin !== baseline.is_admin) out.is_admin = isAdmin;
    if (isHrAdmin !== baseline.is_hr_admin) out.is_hr_admin = isHrAdmin;
    if (isNewsAdmin !== baseline.is_news_admin) out.is_news_admin = isNewsAdmin;
    if (isCeoUpdate !== baseline.is_ceo_update) out.is_ceo_update = isCeoUpdate;
    if (isAlertsAdmin !== baseline.is_alerts_admin) out.is_alerts_admin = isAlertsAdmin;
    if (isManager !== baseline.is_manager) out.is_manager = isManager;
    if (isDm !== baseline.is_dm) out.is_dm = isDm;
    if (isMom !== baseline.is_mom) out.is_mom = isMom;
    if (isEm !== baseline.is_em) out.is_em = isEm;
    if (isDc !== baseline.is_dc) out.is_dc = isDc;
    if (isDf !== baseline.is_df) out.is_df = isDf;
    if (isDvCheckAdmin !== baseline.is_dvcheck_admin) out.is_dvcheck_admin = isDvCheckAdmin;
    if (isLongServiceAdmin !== baseline.is_long_service_admin) out.is_long_service_admin = isLongServiceAdmin;
    if (isMoorslineEditor !== baseline.is_moorsline_editor) out.is_moorsline_editor = isMoorslineEditor;
    if (isVacanciesManager !== !!baseline.is_vacancies_manager) out.is_vacancies_manager = isVacanciesManager;
    if (isNewsletterPublisher !== baseline.is_newsletter_publisher) out.is_newsletter_publisher = isNewsletterPublisher;
    if (isTrainingAdmin !== baseline.is_training_admin) out.is_training_admin = isTrainingAdmin;
    if (isIncidentAdmin !== baseline.is_incident_admin) out.is_incident_admin = isIncidentAdmin;
    if ((lsStartDate || null) !== (baseline.ls_start_date ?? null)) out.lsStartDate = lsStartDate || null;
    if (isFirstAider !== baseline.is_first_aider) out.is_first_aider = isFirstAider;
    if (isDocsEditor !== baseline.is_docs_editor) out.is_docs_editor = isDocsEditor;
    if (isContactEditor !== baseline.is_contact_editor) out.is_contact_editor = isContactEditor;
    if (isOnboardingAdmin !== baseline.is_onboarding_admin) out.is_onboarding_admin = isOnboardingAdmin;
    if (isArticleWriterCeo !== baseline.is_article_writer_ceo) out.is_article_writer_ceo = isArticleWriterCeo;
    if (isArticleWriterWeekly !== baseline.is_article_writer_weekly) out.is_article_writer_weekly = isArticleWriterWeekly;
    if (isWeeklySheetEnhanced !== baseline.is_weekly_sheet_enhanced) {
      out.is_weekly_sheet_enhanced = isWeeklySheetEnhanced;
    }
    if (signInEnabled !== baseline.sign_in_enabled) out.sign_in_enabled = signInEnabled;

    // Apps — send the keys that differ.
    const appsDiff: Partial<AppPermissions> = {};
    let appsChanged = false;
    for (const k of Object.keys(apps) as Array<keyof AppPermissions>) {
      if (apps[k] !== baseline.apps[k]) {
        appsDiff[k] = apps[k];
        appsChanged = true;
      }
    }
    if (appsChanged) out.apps = appsDiff;

    // (Emergency-contact visibility is managed on the People profile drawer now.)

    if (JSON.stringify([...departments].sort()) !== JSON.stringify([...baseline.departments].sort())) {
      out.departments = departments;
    }

    const memberNumeric = memberId.trim() === "" ? null : Number(memberId.trim());
    if (memberNumeric !== baseline.member_id_number) {
      out.member_id_number = memberNumeric;
    }

    // ID Card — admin override of the MERAC card number (digits-only on the wire).
    const meracValue = meracCardNo.replace(/\D/g, "") || null;
    if (meracValue !== (baseline.merac_card_no ?? null)) {
      out.merac_card_no = meracValue;
    }

    if (lineManagerContactId !== baseline.line_manager_contact_id) {
      out.line_manager_contact_id = lineManagerContactId;
    }
    const lmnTrim = lineManagerName.trim() === "" ? null : lineManagerName.trim();
    if (lmnTrim !== (baseline.line_manager_name ?? null)) {
      out.line_manager_name = lmnTrim;
    }

    return out;
  }


  async function save() {
    setSaveError(null);

    if (!editing) {
      if (!first.trim() || !last.trim()) {
        setSaveError("First and last name are required.");
        return;
      }
      if (!EMAIL_REGEX.test(email.trim())) {
        setSaveError("Email is not valid.");
        return;
      }
      if (departments.length === 0) {
        setSaveError("Pick at least one department.");
        return;
      }
    }

    setSaving(true);
    setSaved(false);
    try {
      if (!editing) {
        await apiFetch("/api/access/users", {
          method: "POST",
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            first_name: first.trim(),
            last_name: last.trim(),
            departments,
            is_admin: isAdmin,
          }),
        });
        navigate("/access/users", {
          state: { flash: "User approved. They'll appear in the list after first sign-in." },
        });
        return;
      }

      // Edit mode — validate member ID first.
      if (memberId.trim() !== "") {
        const n = Number(memberId.trim());
        if (!Number.isInteger(n) || n <= 0) {
          setSaveError("Member number must be a positive integer.");
          setSaving(false);
          return;
        }
      }

      // ID Card — the MERAC card number override must be a 9-digit block.
      if (meracCardNo.trim() !== "" && meracCardNo.replace(/\D/g, "").length !== 9) {
        setSaveError("Card number (MERAC) must be 9 digits.");
        setSaving(false);
        return;
      }

      const body = buildPatchBody();
      // Sign-In System (Build 1) -- duty role grants persist via a separate
      // PUT /api/timesheet/approvals. Detect a change so an admin who only
      // toggled role pills still saves.
      const grantsChanged =
        grantedCodes.size !== grantsBaseline.size ||
        [...grantedCodes].some((c) => !grantsBaseline.has(c));
      if (Object.keys(body).length === 0 && !grantsChanged) {
        setSaved(true);
        window.setTimeout(() => setSaved(false), 600);
        setSaving(false);
        return;
      }

      if (Object.keys(body).length > 0) {
        await apiFetch(`/api/access/users/${userId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      }
      if (grantsChanged) {
        await apiFetch(`/api/timesheet/approvals/${userId}`, {
          method: "PUT",
          body: JSON.stringify({ roleCodes: [...grantedCodes] }),
        });
      }
      setSaved(true);
      window.setTimeout(() => {
        navigate("/access/users", { state: { flash: "Changes saved." } });
      }, 600);
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string };
        if (b.error === "LAST_ADMIN") {
          setSaveError("Cannot remove admin from the last admin user.");
        } else {
          setSaveError(e.message);
        }
      } else {
        setSaveError(e instanceof ApiError ? e.message : "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function unlock() {
    if (!editing || userId === null) return;
    setUnlocking(true);
    setUnlockError(null);
    try {
      await apiFetch(`/api/access/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ lockedUntil: null }),
      });
      setLockedUntil(null);
      setIsLocked(false);
    } catch (e) {
      setUnlockError(e instanceof ApiError ? e.message : "Unlock failed.");
    } finally {
      setUnlocking(false);
    }
  }

  function formatLockedUntil(iso: string): string {
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  }

  async function doDelete() {
    if (!editing || userId === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiFetch(`/api/access/users/${userId}`, { method: "DELETE" });
      navigate("/access/users", { state: { flash: "User deleted." } });
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message : "Delete failed.");
      setDeleting(false);
    }
  }

  async function toggleBlock() {
    if (!editing || userId === null) return;
    setBlocking(true);
    setBlockError(null);
    const action = isBlocked ? "unblock" : "block";
    try {
      await apiFetch(`/api/access/users/${userId}/${action}`, { method: "POST" });
      setIsBlocked(!isBlocked);
      if (baseline) setBaseline({ ...baseline, is_blocked: !isBlocked });
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string; detail?: string };
        if (b.error === "LAST_ADMIN") {
          setBlockError("Cannot block the last unblocked admin.");
        } else {
          setBlockError(b.detail ?? e.message);
        }
      } else {
        setBlockError(e instanceof ApiError ? e.message : "Block toggle failed.");
      }
    } finally {
      setBlocking(false);
    }
  }

  async function revealPin() {
    if (userId === null) return;
    setPinModal({ loading: true, pin: null, error: null });
    try {
      const res = await apiFetch<{ pin: string }>(`/api/access/users/${userId}/pin`);
      setPinModal({ loading: false, pin: res.pin, error: null });
    } catch (e) {
      setPinModal({
        loading: false,
        pin: null,
        error: e instanceof ApiError ? e.message : "PIN reveal failed.",
      });
    }
  }

  async function revealEmergency() {
    if (userId === null) return;
    setEmergencyModal({ loading: true, contact: null, empty: false, error: null });
    try {
      const res = await apiFetch<{
        emergency: { name: string | null; relationship: string | null; phone: string | null; alt: string | null } | null;
      }>(`/api/access/users/${userId}/emergency`);
      if (res.emergency === null) {
        setEmergencyModal({ loading: false, contact: null, empty: true, error: null });
      } else {
        setEmergencyModal({ loading: false, contact: res.emergency, empty: false, error: null });
      }
    } catch (e) {
      setEmergencyModal({
        loading: false,
        contact: null,
        empty: false,
        error: e instanceof ApiError ? e.message : "Emergency reveal failed.",
      });
    }
  }

  async function revealAddress() {
    if (userId === null) return;
    setAddressModal({ loading: true, address: null, empty: false, error: null });
    try {
      const res = await apiFetch<{
        address: { line1: string | null; line2: string | null; postcode: string | null; homePhone: string | null } | null;
      }>(`/api/access/users/${userId}/address`);
      if (res.address === null) {
        setAddressModal({ loading: false, address: null, empty: true, error: null });
      } else {
        setAddressModal({ loading: false, address: res.address, empty: false, error: null });
      }
    } catch (e) {
      setAddressModal({
        loading: false,
        address: null,
        empty: false,
        error: e instanceof ApiError ? e.message : "Home address reveal failed.",
      });
    }
  }

  function toggleDept(dept: string) {
    setDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept],
    );
  }

  function toggleApp(key: keyof AppPermissions) {
    if (!apps) return;
    setApps({ ...apps, [key]: !apps[key] });
  }

  // Sign-In System (Build 1) -- toggle a duty role grant (State 18).
  function toggleRole(code: string) {
    setGrantedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <div>
      <TopBar
        title={editing ? "Edit user" : "New user"}
        sub={editing ? `${first} ${last}`.trim() || "Loading..." : "Approve someone to sign in"}
        onBack={() => navigate("/access/users")}
      />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {loadError && (
          <Card>
            <div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{loadError}</div>
          </Card>
        )}

        {loading ? (
          <div
            aria-hidden="true"
            style={{
              height: 480,
              background: C.paperWarm,
              borderRadius: 18,
              border: `1px solid ${C.hairlineLt}`,
            }}
          />
        ) : (
          <>
            {/* Identity + status row */}
            <Card>
              <Eyebrow>Identity</Eyebrow>
              <div style={{ marginTop: 8 }}>
                <Field
                  label="First name"
                  value={first}
                  onChange={setFirst}
                  disabled={editing}
                />
                <Field
                  label="Last name"
                  value={last}
                  onChange={setLast}
                  disabled={editing}
                />
                <Field
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  type="email"
                  ph="firstname.lastname@nymr.co.uk"
                  disabled={editing}
                />
              </div>
              {editing && (
                <>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {signOnStatus === "pending" && <Stamp tone="brass">Pending sign-on</Stamp>}
                    {signOnStatus === "active" && <Stamp tone="sage">Active</Stamp>}
                    {isBlocked && <Stamp tone="signal">Blocked</Stamp>}
                    {hasPin && <Stamp tone="heather">PIN</Stamp>}
                  </div>
                  <div
                    style={{
                      fontFamily: F.serif,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: C.inkMute,
                      lineHeight: 1.45,
                      padding: "8px 2px 0",
                    }}
                  >
                    Identity is owned by Entra (SSO users) or the user&apos;s own Me page (PIN
                    users). Admins cannot rename users from here.
                  </div>
                </>
              )}
            </Card>

            {/* Roles grid */}
            <Card>
              <Eyebrow>Roles</Eyebrow>
              <div
                style={{
                  fontFamily: F.serif,
                  fontStyle: "italic",
                  fontSize: 12,
                  color: C.inkMute,
                  marginTop: 4,
                  marginBottom: 10,
                  lineHeight: 1.45,
                }}
              >
                Each role grants visibility. Toggle the apps the user can open in the next card.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Check label="Admin"
                  checked={isAdmin}
                  onToggle={() => setIsAdmin(!isAdmin)} />
                <Check label="HR Admin"
                  checked={isHrAdmin}
                  onToggle={() => setIsHrAdmin(!isHrAdmin)} />
                <Check label="News Admin"
                  checked={isNewsAdmin}
                  onToggle={() => setIsNewsAdmin(!isNewsAdmin)}
                  disabled={editing && !hasContact} />
                {/* Phase 5d -- CEO Update. With News Admin, unlocks the CEO
                    Newsletter Creator + CEO article routing. Both flags required. */}
                <Check label="CEO Update"
                  sub="With News Admin, unlocks the CEO Newsletter creator & routing."
                  checked={isCeoUpdate}
                  onToggle={() => setIsCeoUpdate(!isCeoUpdate)}
                  disabled={editing && !hasContact} />
                {/* Phase 7 §3.8 — slots between News Admin and Alerts.
                    Sees customer names + green-block notes on the Weekly
                    Sheet. Default OFF for every user. Server emits the
                    dedicated user.perms.weekly_sheet_enhanced audit row
                    on transitions. */}
                <Check label="Weekly Sheet Enhanced Data View"
                  sub="Sees customer names & green-block notes"
                  checked={isWeeklySheetEnhanced}
                  onToggle={() => setIsWeeklySheetEnhanced(!isWeeklySheetEnhanced)} />
                <Check label="Alerts Admin"
                  checked={isAlertsAdmin}
                  onToggle={() => setIsAlertsAdmin(!isAlertsAdmin)}
                  disabled={editing && !hasContact} />
                <Check label="Manager"
                  checked={isManager}
                  onToggle={() => setIsManager(!isManager)}
                  disabled={editing && !hasContact} />
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Duty Scheduler roles</Eyebrow>
                <Check label="Duty Manager (DM)"
                  checked={isDm}
                  onToggle={() => setIsDm(!isDm)} />
                <Check label="Mobile Ops (MOM)"
                  checked={isMom}
                  onToggle={() => setIsMom(!isMom)} />
                <Check label="Event Manager (EM)"
                  checked={isEm}
                  onToggle={() => setIsEm(!isEm)} />
                <Check label="Duty Controller (DC)"
                  checked={isDc}
                  onToggle={() => setIsDc(!isDc)} />
                <Check label="First Aider"
                  checked={isFirstAider}
                  onToggle={() => setIsFirstAider(!isFirstAider)} />
                <Check label="Duty Fitter (DF)"
                  checked={isDf}
                  onToggle={() => setIsDf(!isDf)} />
                {/* Common Duty Phones moved to the Duty Scheduler's own Settings
                    panel (2026-07) — editable there by anyone with Duty App access. */}
                {/* Phase 11 / Q2 + Q3 — content-editor roles. Distinct
                    from operational roles above; grants admin-like
                    write access scoped to one app each. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Check label="Document Editor"
                  sub="Create, edit, and delete documents in any folder."
                  checked={isDocsEditor}
                  onToggle={() => setIsDocsEditor(!isDocsEditor)} />
                <Check label="Onboarding Document Control"
                  sub="Flag documents as onboarding / consent items in the Documents editor. Admins always have this."
                  checked={isOnboardingAdmin}
                  onToggle={() => setIsOnboardingAdmin(!isOnboardingAdmin)} />
                <Check label="Contact Editor"
                  sub="Create, edit, and delete directory contacts (NYMR + volunteer). Personal contacts unaffected."
                  checked={isContactEditor}
                  onToggle={() => setIsContactEditor(!isContactEditor)} />
                {/* Article Writer (migration 070) — which newsletter(s) this
                    person may write articles for. Ticking either box auto-
                    enables the Article Writer app (apps card below), so the
                    tile appears for them without a second toggle. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Article Writer roles</Eyebrow>
                <Check label="Article Writer — CEO Newsletter"
                  sub="May write articles routed to the CEO Newsletter."
                  checked={isArticleWriterCeo}
                  onToggle={() => {
                    const next = !isArticleWriterCeo;
                    setIsArticleWriterCeo(next);
                    if (next && apps && !apps.articleWriter) setApps({ ...apps, articleWriter: true });
                  }} />
                <Check label="Article Writer — Weekly Newsletter"
                  sub="May write articles routed to the Weekly Newsletter."
                  checked={isArticleWriterWeekly}
                  onToggle={() => {
                    const next = !isArticleWriterWeekly;
                    setIsArticleWriterWeekly(next);
                    if (next && apps && !apps.articleWriter) setApps({ ...apps, articleWriter: true });
                  }} />
                <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11.5, color: C.inkMute, marginTop: 8, lineHeight: 1.45 }}>
                  Ticking either box automatically switches on the <strong>Article Writer</strong> app for this user, so the tile appears for them.
                </div>
                {/* Driving Licence Check (migration 074) — DV Admin role. Grants
                    the admin desktop view: roster, manual DVLA verification,
                    suspend/reinstate, and fleet VES compliance. The DRIVER
                    self-service side is the "Driving Licence Check" app toggle
                    in the Apps card below. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Driving Licence Check</Eyebrow>
                <Check label="DV Admin"
                  sub="Verify driver licences, manage the register & fleet compliance."
                  checked={isDvCheckAdmin}
                  onToggle={() => setIsDvCheckAdmin(!isDvCheckAdmin)} />
                {/* Moorsline (migration 094) — Moorsline Editor role. Upload,
                    publish, schedule & delete magazine editions. Reading the
                    magazine is open to everyone (no app gate). */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Moorsline</Eyebrow>
                <Check label="Moorsline Editor"
                  sub="Upload, publish, schedule & delete magazine editions."
                  checked={isMoorslineEditor}
                  onToggle={() => setIsMoorslineEditor(!isMoorslineEditor)} />
                {/* Vacancies (migration 169) — Vacancies Manager role. Runs the
                    desktop Vacancies Manager (edit, publish, applications inbox)
                    without full admin. SSO accounts only; the in-Hub viewer is
                    the "Vacancies" app grant in the Apps card below. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Vacancies</Eyebrow>
                <Check label="Vacancies Manager"
                  sub="Edit & publish vacancies and review applications (NYMR account sign-in only)."
                  checked={isVacanciesManager}
                  onToggle={() => setIsVacanciesManager(!isVacanciesManager)} />
                {/* NYMR Newsletter (migration 124) — Newsletter Publisher role.
                    Upload, publish, schedule & delete newsletter editions. Reading
                    is the on-by-default "Newsletter Reader" app grant (Apps section). */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>NYMR Newsletter</Eyebrow>
                <Check label="Newsletter Publisher"
                  sub="Upload, publish, schedule & delete newsletter editions."
                  checked={isNewsletterPublisher}
                  onToggle={() => setIsNewsletterPublisher(!isNewsletterPublisher)} />
                {/* Training & Competencies (migration 099) — Training Admin role.
                    Grants the Training admin console (iHasco register, Unmatched
                    imports matching queue, audit) without full admin / HR-admin.
                    My Training for every user is the apps.training flag (derived
                    server-side once matched), not a role flag here. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Training &amp; Competencies</Eyebrow>
                <Check label="Training Admin"
                  sub="Manage the iHasco register, the Unmatched imports matching queue & audit."
                  checked={isTrainingAdmin}
                  onToggle={() => setIsTrainingAdmin(!isTrainingAdmin)} />
                {/* Incident Log (migration 101) — FULL ACCESS role. Raise / edit /
                    delete incidents, manage documents (incl. discrete security),
                    tasks, per-record access & convert messages. READ ONLY (view all
                    incidents) is the "Incident Log (Read Only)" app toggle above. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Incident Log (H&amp;S)</Eyebrow>
                <Check label="Incident Log — Full Access"
                  sub="Raise, edit & manage incidents, documents, tasks, per-record access & message conversion."
                  checked={isIncidentAdmin}
                  onToggle={() => setIsIncidentAdmin(!isIncidentAdmin)} />
                {/* Long Service Awards (migration 085) — LS Admin role. Grants
                    the LS Admin console: enrolment, service periods, the
                    production/print queue, and award settings. The RECIPIENT
                    self-service side is the "Long Service Pass" app toggle in
                    the Apps card below. */}
                <Hairline style={{ margin: "6px 0" }} />
                <Eyebrow>Long Service Awards</Eyebrow>
                <Check label="Long Service Admin"
                  sub="Manage enrolment, service periods, production & award settings."
                  checked={isLongServiceAdmin}
                  onToggle={() => setIsLongServiceAdmin(!isLongServiceAdmin)} />
                <div style={{ marginTop: 10, maxWidth: 240 }}>
                  <div style={{ fontFamily: F.body, fontWeight: 700, fontSize: 12, color: C.inkSoft, marginBottom: 5 }}>Long Service start date</div>
                  <input type="date" value={lsStartDate} onChange={(e) => setLsStartDate(e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.hairline}`, background: C.card, fontFamily: F.mono, fontSize: 13.5, color: C.ink, outline: "none" }} />
                </div>
                <div style={{ fontFamily: F.body, fontWeight: 600, fontSize: 11.5, color: C.inkMute, marginTop: 6, lineHeight: 1.5 }}>
                  Setting a start date gives this person their first service period and makes them visible in the Long Service Admin list. Full service history is managed there; clearing this field does not remove it.
                </div>
              </div>
              {editing && !hasContact && (
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 12,
                    color: C.inkMute,
                    marginTop: 10,
                    lineHeight: 1.45,
                  }}
                >
                  News / Alerts / Manager need a contacts directory row. Add this user to
                  Contacts first to enable these toggles.
                </div>
              )}
            </Card>

            {/* Apps grid */}
            {editing && apps && (
              <Card>
                <Eyebrow>Apps</Eyebrow>
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 12,
                    color: C.inkMute,
                    marginTop: 4,
                    marginBottom: 10,
                    lineHeight: 1.45,
                  }}
                >
                  Hide tiles from this user. Admin-only apps still need the matching role.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {APP_KEYS.map((a) => (
                    <Check
                      key={a.key}
                      label={a.label}
                      sub={a.sub}
                      checked={apps[a.key]}
                      onToggle={() => toggleApp(a.key)}
                    />
                  ))}
                </div>

                {/* BigPlanSTN signal-box assignment — appears once the user holds the
                    signal-box sign-in grant. Saves immediately to its own table. */}
                {userId !== null && apps.bigPlanStn && <BigPlanBoxAssign userId={userId} />}

                {/* Reports applet -- Reports app + the indented Executive
                    sub-permission. Executive requires Reports: turning Reports
                    off forces Executive off, and the Executive toggle is inert
                    until Reports is on. The API write path coerces this too. */}
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  <Check
                    label="Reports app"
                    sub="Enables the standard reporting app and its Home page slice."
                    checked={apps.reports}
                    onToggle={() => {
                      const next = !apps.reports;
                      setApps({ ...apps, reports: next, reportsExec: next ? apps.reportsExec : false });
                    }}
                  />
                  <div style={{ marginLeft: 16, paddingLeft: 14, borderLeft: `2px solid ${apps.reports ? C.brassLt : C.hairlineLt}` }}>
                    <div style={{ opacity: apps.reports ? 1 : 0.5, pointerEvents: apps.reports ? "auto" : "none" }}>
                      <Check
                        label="Executive view"
                        sub="Adds the Senior Level Executive reporting tab inside the Reports app."
                        checked={apps.reportsExec}
                        onToggle={() => setApps({ ...apps, reportsExec: !apps.reportsExec })}
                      />
                    </div>
                    {!apps.reports && (
                      <div style={{ fontStyle: "italic", fontSize: 11.5, color: C.inkMute, marginTop: 6, lineHeight: 1.4 }}>
                        Turn on the Reports app first to grant Executive view.
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Sign-In for duty (State 18). The whole eligibility decision lives
                here; the end user never sees any of it until all three of
                {sign-in enabled, a granted role, a role with a default location}
                are true. */}
            {editing && (
              <Card>
                <Eyebrow>Sign-In for duty</Eyebrow>
                <div style={{ marginTop: 10 }}>
                  <Check
                    label="Sign-in for duty"
                    sub="Shows the duty button on Home and unlocks Group QuickMessage."
                    checked={signInEnabled}
                    onToggle={() => setSignInEnabled(!signInEnabled)}
                  />
                </div>

                <Hairline style={{ margin: "12px 0" }} />
                <Eyebrow style={{ marginBottom: 8 }}>Duty roles</Eyebrow>
                {dutyRoles === null ? (
                  <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkMute, padding: "6px 0" }}>
                    Loading roles…
                  </div>
                ) : dutyRoles.length === 0 ? (
                  <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: C.inkMute, padding: "6px 0" }}>
                    No duty roles in the catalogue. Add some in System Preferences.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dutyRoles.map((r) => {
                      const on = grantedCodes.has(r.code);
                      return (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => toggleRole(r.code)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: 999,
                            border: on ? `1.5px solid ${C.sageDk}` : `1px solid ${C.hairline}`,
                            background: on ? C.sageDk : C.card,
                            color: on ? "#fff" : C.inkSoft,
                            fontFamily: F.body,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <span>{r.displayName}</span>
                          {on && r.lastUsedAt && (
                            <span style={{ fontFamily: F.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, opacity: 0.85, textTransform: "uppercase" }}>
                              Last · {formatShortDate(r.lastUsedAt)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {(() => {
                  const hasRole = grantedCodes.size > 0;
                  const hasLocation = (dutyRoles ?? []).some((r) => grantedCodes.has(r.code) && r.defaultLocationCode);
                  const ready = signInEnabled && hasRole && hasLocation;
                  const needs: string[] = [];
                  if (!signInEnabled) needs.push("sign-in enabled");
                  if (!hasRole) needs.push("a role");
                  else if (!hasLocation) needs.push("a role with a default location");
                  return (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "8px 12px",
                        borderRadius: 10,
                        background: ready ? C.sageBg : C.brassBg,
                        border: `1px solid ${ready ? C.sageLt : C.brassLt}`,
                        color: ready ? C.sageDk : C.brassDk,
                        fontFamily: F.body,
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      {ready ? "Ready to sign in" : `Needs: ${needs.join(", ")}`}
                    </div>
                  );
                })()}

                <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12, color: C.inkMute, marginTop: 10, lineHeight: 1.45 }}>
                  Default locations come from each role (set in System Preferences). Changes take
                  effect from the user&apos;s next sign-in.
                </div>
              </Card>
            )}

            {/* Departments — options from GET /api/access/departments via
                useDepartments (issue #15), unioned with any stale departments
                the user still holds so they stay visible and de-selectable. */}
            <Card>
              <Eyebrow>Departments</Eyebrow>
              {allDepartments === null ? (
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: C.inkMute,
                    padding: "10px 2px 2px",
                  }}
                >
                  Loading departments…
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {deptOptions.map((d) => {
                    const on = departments.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleDept(d)}
                        style={{
                          padding: "7px 12px",
                          borderRadius: 999,
                          border: on ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
                          background: on ? C.brassBg : C.card,
                          color: on ? C.brassDk : C.inkSoft,
                          fontFamily: F.body,
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Account status — block + locked */}
            {editing && (
              <Card>
                <Eyebrow>Account status</Eyebrow>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                  <Check
                    label={isBlocked ? "Blocked from signing in" : "Block sign-in"}
                    checked={isBlocked}
                    onToggle={toggleBlock}
                    disabled={blocking}
                  />
                  {blockError && (
                    <div
                      style={{
                        padding: 10,
                        borderRadius: 10,
                        background: C.signalBg,
                        border: `1px solid rgba(184,58,43,0.22)`,
                        color: C.signalDk,
                        fontFamily: F.body,
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {blockError}
                    </div>
                  )}
                  <Hairline />
                  <Check
                    label="Locked out (3 wrong PINs)"
                    checked={isLocked}
                    onToggle={isLocked && !unlocking ? unlock : () => {}}
                    disabled={!isLocked || unlocking}
                  />
                  {isLocked && lockedUntil && (
                    <div
                      style={{
                        fontFamily: F.serif,
                        fontStyle: "italic",
                        fontSize: 12,
                        color: C.signalDk,
                        padding: "0 28px",
                        fontWeight: 600,
                      }}
                    >
                      Until {formatLockedUntil(lockedUntil)}
                    </div>
                  )}
                  {unlockError && (
                    <div
                      style={{
                        padding: 10,
                        borderRadius: 10,
                        background: C.signalBg,
                        border: `1px solid rgba(184,58,43,0.22)`,
                        color: C.signalDk,
                        fontFamily: F.body,
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {unlockError}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Sensitive reveals */}
            {editing && (
              <Card>
                <Eyebrow>Sensitive data</Eyebrow>
                <div
                  style={{
                    fontFamily: F.serif,
                    fontStyle: "italic",
                    fontSize: 12,
                    color: C.inkMute,
                    marginTop: 4,
                    marginBottom: 10,
                    lineHeight: 1.45,
                  }}
                >
                  These actions are recorded in the audit log.
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  {hasPin && (
                    <Button variant="secondary" onClick={revealPin}>
                      Reveal PIN
                    </Button>
                  )}
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Button variant="secondary" onClick={revealEmergency}>
                      Reveal emergency contact
                    </Button>
                    <Stamp tone="signal">HR only</Stamp>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Button variant="secondary" onClick={revealAddress}>
                      Reveal home address
                    </Button>
                    <Stamp tone="signal">HR only</Stamp>
                  </div>
                </div>
              </Card>
            )}

            {/* Emergency contact visibility moved to the People profile drawer
                (Emergency Access card). Managed there now, not on this page. */}

            {/* NYMR membership */}
            {editing && (
              <Card>
                <Eyebrow>NYMR membership</Eyebrow>
                <div style={{ marginTop: 8 }}>
                  <Field
                    label="Member number"
                    value={memberId}
                    onChange={setMemberId}
                    type="number"
                    ph="e.g. 12345"
                  />
                </div>

                {/* ID Card app (migration 063) — admin override of the MERAC
                    card number. Normally MERAC-synced; set here only to fix or
                    pre-fill it. Wins over the cardholder's self-entry. */}
                <div>
                  <Field
                    label="Card number (MERAC)"
                    value={meracCardNo}
                    onChange={setMeracCardNo}
                    type="text"
                    ph="e.g. 372480505"
                  />
                  <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 600, color: C.inkMute, marginTop: -8, marginBottom: 4, lineHeight: 1.5 }}>
                    The 9-digit card number shown on the ID Card app. Normally synced from MERAC — set it here only to fix or pre-fill it. Overrides the cardholder's self-entered number.
                  </div>
                </div>

                {/* Phase 5d -- forensic Edition Ref. Read-only; stamped into the
                    watermarked CEO Newsletter PDF this user downloads. Never editable. */}
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.inkMute, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                    Edition Ref
                  </div>
                  <div
                    style={{
                      fontFamily: F.mono,
                      fontSize: 14,
                      fontWeight: 700,
                      color: editionRef ? C.ink : C.inkMute,
                      letterSpacing: 0.5,
                      padding: "9px 12px",
                      background: C.paperWarm,
                      border: `1px solid ${C.hairlineLt}`,
                      borderRadius: 10,
                    }}
                  >
                    {editionRef ?? "—"}
                  </div>
                  <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 600, color: C.inkMute, marginTop: 4, lineHeight: 1.5 }}>
                    Stable forensic reference watermarked into the CEO Newsletter PDFs this person downloads. Read-only.
                  </div>
                </div>

                <Hairline style={{ margin: "10px 0 12px" }} />

                <Eyebrow style={{ marginBottom: 8 }}>Line manager</Eyebrow>
                {contacts === null ? (
                  <div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, padding: "8px 0" }}>
                    Loading managers...
                  </div>
                ) : managerOptions.length === 0 ? (
                  <div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, padding: "8px 0" }}>
                    No managers in the contacts directory.
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {managerOptions.map((m) => {
                      const active = lineManagerContactId === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setLineManagerContactId(active ? null : m.id)}
                          style={{
                            padding: "9px 12px",
                            borderRadius: 10,
                            border: active ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
                            background: active ? C.brassBg : C.card,
                            color: active ? C.brassDk : C.ink,
                            fontFamily: F.body,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {m.first} {m.last}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ marginTop: 12 }}>
                  <Field
                    label="Or type a name"
                    value={lineManagerName}
                    onChange={setLineManagerName}
                    ph="Manager not in the contacts directory"
                    max={200}
                  />
                </div>
              </Card>
            )}

            {saveError && (
              <Card>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: C.signalBg,
                    border: `1px solid rgba(184,58,43,0.22)`,
                    color: C.signalDk,
                    fontFamily: F.body,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {saveError}
                </div>
              </Card>
            )}

            <Button
              variant={saved ? "sage" : "primary"}
              fullWidth
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving..." : saved ? "✓ Saved" : editing ? "Save changes" : "Create user"}
            </Button>

            <Button
              variant="secondary"
              fullWidth
              disabled={saving}
              onClick={() => navigate("/access/users")}
            >
              Cancel
            </Button>

            {editing && (
              <Card>
                <Eyebrow c={C.signalDk}>Danger zone</Eyebrow>
                {deleteError && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      background: C.signalBg,
                      border: `1px solid rgba(184,58,43,0.22)`,
                      color: C.signalDk,
                      fontFamily: F.body,
                      fontWeight: 700,
                      fontSize: 13,
                      marginTop: 10,
                    }}
                  >
                    {deleteError}
                  </div>
                )}
                {!confirmDelete ? (
                  <Button
                    variant="danger"
                    fullWidth
                    style={{ marginTop: 12 }}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete user
                  </Button>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        fontFamily: F.serif,
                        fontSize: 14,
                        color: C.ink,
                        fontWeight: 700,
                        marginBottom: 10,
                        lineHeight: 1.4,
                      }}
                    >
                      Delete {first} {last}? Their personal details, PIN, and audit references
                      will be cascade-deleted. This cannot be undone.
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <Button
                        variant="danger"
                        fullWidth
                        disabled={deleting}
                        onClick={doDelete}
                      >
                        {deleting ? "Deleting..." : "Confirm delete"}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={deleting}
                        onClick={() => {
                          setConfirmDelete(false);
                          setDeleteError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </div>

      {pinModal && (
        <PinRevealModal
          state={pinModal}
          onClose={() => setPinModal(null)}
        />
      )}

      {emergencyModal && (
        <EmergencyRevealModal
          state={emergencyModal}
          onClose={() => setEmergencyModal(null)}
        />
      )}

      {addressModal && (
        <AddressRevealModal
          state={addressModal}
          onClose={() => setAddressModal(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PinRevealModal — 30s auto-close, copy-to-clipboard, audited server-side
// ---------------------------------------------------------------------------

function PinRevealModal({
  state,
  onClose,
}: {
  state: { loading: boolean; pin: string | null; error: string | null };
  onClose: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!state.pin) return;
    const t = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(t);
          onClose();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [state.pin, onClose]);

  function copy() {
    if (!state.pin) return;
    navigator.clipboard.writeText(state.pin).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <Eyebrow>PIN reveal</Eyebrow>
      {state.loading && (
        <div style={{ padding: "24px 0", textAlign: "center", color: C.inkSoft, fontFamily: F.body, fontWeight: 700 }}>
          Loading PIN…
        </div>
      )}
      {state.error && (
        <div
          style={{
            padding: 12,
            borderRadius: 10,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
            margin: "10px 0",
          }}
        >
          {state.error}
        </div>
      )}
      {state.pin && (
        <>
          <button
            type="button"
            onClick={copy}
            style={{
              width: "100%",
              padding: "20px 16px",
              borderRadius: 14,
              background: C.brassBg,
              border: `2px solid ${C.brassDk}`,
              fontFamily: F.mono,
              fontSize: 32,
              fontWeight: 800,
              color: C.brassDk,
              letterSpacing: 4,
              cursor: "pointer",
              marginTop: 12,
            }}
          >
            {state.pin}
          </button>
          <div
            style={{
              textAlign: "center",
              fontFamily: F.body,
              fontSize: 12,
              color: copied ? C.sageDk : C.inkSoft,
              fontWeight: 700,
              marginTop: 8,
            }}
          >
            {copied ? "Copied to clipboard." : "Tap to copy"}
          </div>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
              lineHeight: 1.45,
              marginTop: 14,
              textAlign: "center",
            }}
          >
            Auto-closes in {secondsLeft}s. This action has been recorded in the audit log.
          </div>
        </>
      )}
      <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={onClose}>
        Close
      </Button>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// EmergencyRevealModal — HR / self / granted only; audited
// ---------------------------------------------------------------------------

function EmergencyRevealModal({
  state,
  onClose,
}: {
  state: {
    loading: boolean;
    contact: { name: string | null; relationship: string | null; phone: string | null; alt: string | null } | null;
    empty: boolean;
    error: string | null;
  };
  onClose: () => void;
}) {
  return (
    <ModalShell onClose={onClose}>
      <Eyebrow>Emergency contact</Eyebrow>
      {state.loading && (
        <div style={{ padding: "24px 0", textAlign: "center", color: C.inkSoft, fontFamily: F.body, fontWeight: 700 }}>
          Loading…
        </div>
      )}
      {state.error && (
        <div
          style={{
            padding: 12,
            borderRadius: 10,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
            margin: "10px 0",
          }}
        >
          {state.error}
        </div>
      )}
      {state.empty && (
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 14,
            color: C.inkMute,
            padding: "12px 0",
            textAlign: "center",
          }}
        >
          No emergency contact on record.
        </div>
      )}
      {state.contact && (
        <div style={{ marginTop: 10 }}>
          {state.contact.name && (
            <Row label="Name" value={state.contact.name} />
          )}
          {state.contact.relationship && (
            <Row label="Relationship" value={state.contact.relationship} />
          )}
          {state.contact.phone && (
            <Row label="Phone" value={state.contact.phone} mono />
          )}
          {state.contact.alt && (
            <Row label="Alt phone" value={state.contact.alt} mono />
          )}
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
              lineHeight: 1.45,
              marginTop: 14,
              textAlign: "center",
            }}
          >
            This action has been recorded in the audit log.
          </div>
        </div>
      )}
      <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={onClose}>
        Close
      </Button>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// AddressRevealModal — HR / self only; audited; strictest tier
// ---------------------------------------------------------------------------

function AddressRevealModal({
  state,
  onClose,
}: {
  state: {
    loading: boolean;
    address: { line1: string | null; line2: string | null; postcode: string | null; homePhone: string | null } | null;
    empty: boolean;
    error: string | null;
  };
  onClose: () => void;
}) {
  return (
    <ModalShell onClose={onClose}>
      <Eyebrow>Home address</Eyebrow>
      {state.loading && (
        <div style={{ padding: "24px 0", textAlign: "center", color: C.inkSoft, fontFamily: F.body, fontWeight: 700 }}>
          Loading…
        </div>
      )}
      {state.error && (
        <div
          style={{
            padding: 12,
            borderRadius: 10,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
            margin: "10px 0",
          }}
        >
          {state.error}
        </div>
      )}
      {state.empty && (
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 14,
            color: C.inkMute,
            padding: "12px 0",
            textAlign: "center",
          }}
        >
          No home address on record.
        </div>
      )}
      {state.address && (
        <div style={{ marginTop: 10 }}>
          {state.address.line1 && (
            <Row label="Line 1" value={state.address.line1} />
          )}
          {state.address.line2 && (
            <Row label="Line 2" value={state.address.line2} />
          )}
          {state.address.postcode && (
            <Row label="Postcode" value={state.address.postcode} mono />
          )}
          {state.address.homePhone && (
            <Row label="Home phone" value={state.address.homePhone} mono />
          )}
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 12,
              color: C.inkMute,
              lineHeight: 1.45,
              marginTop: 14,
              textAlign: "center",
            }}
          >
            This action has been recorded in the audit log.
          </div>
        </div>
      )}
      <Button variant="secondary" fullWidth style={{ marginTop: 14 }} onClick={onClose}>
        Close
      </Button>
    </ModalShell>
  );
}

// Sign-In System (Build 1) -- "LAST · 12 OCT" caption under granted role pills.
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "100px 1fr",
        gap: 12,
        padding: "8px 0",
        borderBottom: `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        style={{
          fontFamily: F.body,
          fontSize: 11,
          fontWeight: 800,
          color: C.inkMute,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? F.mono : F.body,
          fontSize: 14,
          fontWeight: 700,
          color: C.ink,
          letterSpacing: mono ? 0.5 : 0,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43,32,20,0.55)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          borderRadius: 18,
          padding: "20px 18px",
          maxWidth: 420,
          width: "100%",
          boxShadow: C.shadowL,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// BigPlanSTN signal-box assignment — which signal box(es) a BigPlanSTN user may sign in at on the
// iPad. Loads the box catalogue + the user's current set, and saves immediately on each toggle.
function BigPlanBoxAssign({ userId }: { userId: number }) {
  const [boxes, setBoxes] = useState<Array<{ id: string; name: string }>>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [b, u] = await Promise.all([
          apiFetch<{ boxes: Array<{ id: string; name: string; sort: number }> }>("/api/bigplan/boxes"),
          apiFetch<{ boxes: string[] }>(`/api/bigplan/users/${userId}/boxes`),
        ]);
        if (off) return;
        setBoxes(b.boxes); setSelected(u.boxes);
      } catch { /* non-fatal */ }
    })();
    return () => { off = true; };
  }, [userId]);
  const toggle = async (id: string) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    setSelected(next); setSaving(true);
    try { await apiFetch(`/api/bigplan/users/${userId}/boxes`, { method: "PUT", body: JSON.stringify({ boxIds: next }) }); }
    catch { /* keep optimistic state; admin can retry */ } finally { setSaving(false); }
  };
  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: C.brassSurf, border: `1px solid ${C.brassLt}` }}>
      <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.brassDk, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 4 }}>
        Signal-box assignment{saving ? " · saving…" : ""}
      </div>
      <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkMute, marginBottom: 10, lineHeight: 1.45 }}>
        Which signal box(es) this person may sign in at on the iPad. They pick which one when they sign in.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
        {boxes.length === 0 && <span style={{ fontFamily: F.body, fontSize: 12, color: C.inkMute }}>Loading boxes…</span>}
        {boxes.map((b) => {
          const on = selected.includes(b.id);
          return (
            <button key={b.id} type="button" onClick={() => toggle(b.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 999, cursor: "pointer", border: `1.5px solid ${on ? C.brassDk : C.hairline}`, background: on ? C.brassBg : C.card, fontFamily: F.body, fontSize: 12, fontWeight: 800, color: on ? C.brassDk : C.inkMute }}>
              {b.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
